import { createContext, useContext } from 'react';
import type { PolicyModeType } from '@kirin/api-client/contracts/policies';

export interface ParentPolicyGate {
  /**
   * The PARENT policy's Off/Monitor/Enforce mode. A nested mode-valued
   * sub-policy keeps its own mode, but the parent is still a master switch:
   * parent Off means the sub-policy cannot run at all (the backend's
   * `computeToolCallScanMode` and the agent's `ToolCallScanModeOf` both
   * return Off in that case), so its control must read as disabled.
   */
  mode?: PolicyModeType;
  /** Parent policy display name, for the "disabled because …" copy. */
  name?: string;
}

/**
 * The parent policy's mode, for nested mode-valued sub-policies (KIR-4858).
 *
 * Same shape of problem — and the same solution — as
 * {@link import('./enforceAvailability').EnforceAvailabilityContext}: a nested
 * sub-policy renders through `PolicySettingField`, which has ~14 call sites in
 * `PolicySettingsForm` and no access to the policy object, so a context keeps
 * the gate in one place instead of threading a prop through all of them.
 *
 * Defaults to an EMPTY gate (no mode) so a form that never provides it — or a
 * field rendered in isolation, e.g. in a test — behaves exactly as before.
 */
export const ParentPolicyModeContext = createContext<ParentPolicyGate>({});

export function useParentPolicyMode(): ParentPolicyGate {
  return useContext(ParentPolicyModeContext);
}
